import { useState } from "react";
import { Button, Card, EmptyState, PageHead, SelectX, Skeleton, Timeline } from "@guestpilot/ui-kit";
import { useGetPages } from "../../api/hooks";
import type { Schema, Team } from "../../api/types";
import { formatDateTime } from "../../format";
import { useI18n } from "../../i18n";
import { useSession } from "../../session";

type Audit = Schema<"AuditEvent">;

const FILTERS = ["", "review.", "removal.", "exception.", "campaign.", "scan.", "team."] as const;

/** Full audit trail of the Team, newest first, filterable by kind of action (BR-113). */
export function History({ team }: { team: Team }) {
  const { t, locale } = useI18n();
  const s = useSession();
  const tz = s.me.tenant.timeZone;
  const [filter, setFilter] = useState("");
  const q = useGetPages<Audit>(`/api/v1/teams/${team.id}/history`, {
    action: filter || undefined,
    limit: 25,
  });
  const label = (a: string | undefined) => {
    const key = `ui:audit.${a}`;
    const v = t(key);
    return v === key ? (a ?? "") : v;
  };
  return (
    <div className="gp-page gp-page--narrow">
      <PageHead
        title={t("ui:history.title")}
        subtitle={t("ui:history.lead")}
        actions={
          <SelectX
            aria-label={t("ui:history.filter")}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            options={FILTERS.map((f) => ({
              value: f,
              label: f ? t(`ui:history.kind.${f.replace(".", "")}`) : t("ui:history.all"),
            }))}
          />
        }
      />
      {q.isLoading ? <Skeleton rows={5} /> : null}
      {q.items && q.items.length === 0 ? <EmptyState title={t("ui:history.empty")} /> : null}
      {q.items && q.items.length > 0 ? (
        <Card>
          <Timeline
            events={q.items.map((e) => ({
              id: e.id ?? "",
              summary: label(e.action),
              meta: [e.actor?.displayName ?? t(`ui:history.actor.${e.actorType ?? "system"}`)].join(" · "),
              at: e.occurredAt ?? "",
              atLabel: formatDateTime(e.occurredAt, locale, tz),
            }))}
          />
          {q.hasNextPage ? (
            <Button onClick={() => void q.fetchNextPage()}>{t("ui:users.loadMore")}</Button>
          ) : null}
        </Card>
      ) : null}
    </div>
  );
}
